import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MarcaSiNo, SelectorSiNo } from '../../components/SiNo';

// Ficha de Seguimiento de un egresado de la Red (punto 7 de José, 25/09/2026).
// La misma pantalla sirve para Ver (`soloLectura`) y para Actualizar.

const VACIO = {
  aplica_red: null,
  nivel_instruccion_id: '',
  trabaja: null,
  empresa: '',
  cargo: '',
  trabaja_en_area: null,
  emprende: null,
  emprendimiento: '',
  comentarios: '',
  actividades: [],
};

const Campo = ({ etiqueta, children }) => (
  <div className="mb-3">
    <Form.Label className="fw-semibold mb-1">{etiqueta}</Form.Label>
    <div>{children}</div>
  </div>
);

const SeguimientoEgresado = ({ soloLectura = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [persona, setPersona] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [info, setInfo] = useState(null); // actualizado / actualizado_por / nombres de actividades
  const [opciones, setOpciones] = useState({ nivelInstruccion: [], actividades: [] });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        const [ficha, ops] = await Promise.all([
          axios.get(`/seguimiento-egresados/${id}`, { headers }),
          axios.get('/seguimiento-egresados-opciones', { headers }),
        ]);
        setPersona(ficha.data.persona);
        setOpciones(ops.data);
        const s = ficha.data.seguimiento;
        if (s) {
          setForm({
            ...VACIO,
            ...s,
            nivel_instruccion_id: s.nivel_instruccion_id ?? '',
            empresa: s.empresa ?? '',
            cargo: s.cargo ?? '',
            emprendimiento: s.emprendimiento ?? '',
            comentarios: s.comentarios ?? '',
            actividades: (s.actividades || []).map(Number),
          });
          setInfo(s);
        }
      } catch (e) {
        console.error('Error cargando el seguimiento:', e);
        setError(e?.response?.status === 404 ? 'Participante no encontrado.' : 'No se pudo cargar la ficha.');
      } finally {
        setCargando(false);
      }
    };
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const poner = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const alternarActividad = (actividadId) => {
    setForm((prev) => ({
      ...prev,
      actividades: prev.actividades.includes(actividadId)
        ? prev.actividades.filter((a) => a !== actividadId)
        : [...prev.actividades, actividadId],
    }));
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.put(`/seguimiento-egresados/${id}`, {
        ...form,
        nivel_instruccion_id: form.nivel_instruccion_id === '' ? null : Number(form.nivel_instruccion_id),
        empresa: form.empresa || null,
        cargo: form.cargo || null,
        emprendimiento: form.emprendimiento || null,
        comentarios: form.comentarios || null,
      }, { headers });
      toast.success('Seguimiento guardado');
      // Vuelve al listado en la misma página (ver useListadoRecordado).
      setTimeout(() => navigate(-1), 600);
    } catch (err) {
      console.error('Error guardando el seguimiento:', err);
      const errores = err?.response?.data?.errors;
      toast.error(errores ? Object.values(errores)[0][0] : 'No se pudo guardar el seguimiento');
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  }
  if (error) {
    return (
      <div className="card-box mt-4" style={{ padding: '20px' }}>
        <div className="text-danger mb-3">{error}</div>
        <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
      </div>
    );
  }

  const nivelActual = opciones.nivelInstruccion.find((n) => String(n.id) === String(form.nivel_instruccion_id));

  return (
    <div className="container-fluid mt-2" style={{ fontSize: '0.9rem' }}>
      <div className="col-lg-9 mx-auto">
        <div className="card-box" style={{ padding: '20px' }}>
          <h2 className="mb-1">{soloLectura ? 'Seguimiento del Egresado' : 'Actualizar Seguimiento'}</h2>
          <div className="text-muted mb-3">
            <strong>{persona.nombres} {persona.apellidos}</strong> · C.I. {persona.cedula_identidad}
            {persona.edad !== null && <> · {persona.edad} años</>}
            {persona.genero && <> · {persona.genero}</>}
            <br />
            {persona.telefono_celular && <>Tel. {persona.telefono_celular} · </>}
            {persona.direccion_email}
            <br />
            Nivel de instrucción en su ficha de participante: {persona.nivel_instruccion_ficha || '—'}
          </div>
          {info?.actualizado && (
            <div className="small text-muted mb-3">
              Última actualización: {info.actualizado}{info.actualizado_por ? ` por ${info.actualizado_por}` : ''}
            </div>
          )}

          <Form onSubmit={guardar}>
            <Campo etiqueta="¿Aplica a la Red de egresados?">
              {soloLectura
                ? <><MarcaSiNo valor={form.aplica_red} si="Aplica" no="No aplica" /> {form.aplica_red === true ? 'Aplica' : form.aplica_red === false ? 'No aplica' : 'Sin evaluar'}</>
                : <SelectorSiNo valor={form.aplica_red} onChange={(v) => poner('aplica_red', v)} si="Aplica" no="No aplica" />}
            </Campo>

            <Campo etiqueta="Nivel de instrucción actual">
              {soloLectura
                ? (nivelActual?.descripcion || '—')
                : (
                  <Form.Select value={form.nivel_instruccion_id} onChange={(e) => poner('nivel_instruccion_id', e.target.value)} style={{ maxWidth: '400px' }}>
                    <option value="">Sin dato</option>
                    {opciones.nivelInstruccion.map((n) => <option key={n.id} value={n.id}>{n.descripcion}</option>)}
                  </Form.Select>
                )}
            </Campo>

            <h5 className="mt-4">Información laboral</h5>
            <Campo etiqueta="¿Trabaja actualmente?">
              {soloLectura
                ? <MarcaSiNo valor={form.trabaja} />
                : <SelectorSiNo valor={form.trabaja} onChange={(v) => poner('trabaja', v)} />}
            </Campo>
            {form.trabaja === true && (
              <>
                <div className="d-flex flex-wrap" style={{ gap: '15px' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <Campo etiqueta="Empresa / institución">
                      {soloLectura ? (form.empresa || '—') : (
                        <Form.Control value={form.empresa} maxLength={255} onChange={(e) => poner('empresa', e.target.value)} />
                      )}
                    </Campo>
                  </div>
                  <div style={{ flex: '1 1 250px' }}>
                    <Campo etiqueta="Cargo">
                      {soloLectura ? (form.cargo || '—') : (
                        <Form.Control value={form.cargo} maxLength={255} onChange={(e) => poner('cargo', e.target.value)} />
                      )}
                    </Campo>
                  </div>
                </div>
                <Campo etiqueta="¿Trabaja en el área de lo que estudió?">
                  {soloLectura
                    ? <MarcaSiNo valor={form.trabaja_en_area} />
                    : <SelectorSiNo valor={form.trabaja_en_area} onChange={(v) => poner('trabaja_en_area', v)} />}
                </Campo>
              </>
            )}

            <h5 className="mt-4">Emprendimiento</h5>
            <Campo etiqueta="¿Tiene un emprendimiento?">
              {soloLectura
                ? <MarcaSiNo valor={form.emprende} />
                : <SelectorSiNo valor={form.emprende} onChange={(v) => poner('emprende', v)} />}
            </Campo>
            {form.emprende === true && (
              <Campo etiqueta="¿De qué se trata?">
                {soloLectura ? (form.emprendimiento || '—') : (
                  <Form.Control value={form.emprendimiento} maxLength={255} onChange={(e) => poner('emprendimiento', e.target.value)} />
                )}
              </Campo>
            )}

            <h5 className="mt-4">¿En qué actividades le gustaría participar?</h5>
            <div className="d-flex flex-wrap mb-3" style={{ gap: '6px 20px' }}>
              {opciones.actividades.map((a) => (
                <Form.Check
                  key={a.id}
                  type="checkbox"
                  id={`actividad-${a.id}`}
                  label={a.descripcion}
                  checked={form.actividades.includes(Number(a.id))}
                  onChange={() => alternarActividad(Number(a.id))}
                  disabled={soloLectura}
                />
              ))}
            </div>

            <Campo etiqueta="Comentarios">
              {soloLectura
                ? <div style={{ whiteSpace: 'pre-wrap' }}>{form.comentarios || '—'}</div>
                : <Form.Control as="textarea" rows={4} value={form.comentarios} maxLength={5000}
                    onChange={(e) => poner('comentarios', e.target.value)} placeholder="Información adicional del egresado" />}
            </Campo>

            <div className="d-flex justify-content-end mt-4" style={{ gap: '10px' }}>
              <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
              {soloLectura
                ? <Button variant="warning" onClick={() => navigate(`/seguimiento-egresados/${id}/edit`, { replace: true })}>Actualizar</Button>
                : <Button variant="success" type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>}
            </div>
          </Form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SeguimientoEgresado;
